import {
	BusyIndicator,
	ContextActionBar,
	NoResults
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement, useMemo } from "react";
import { Details } from "../Details/Details";
import { useFetchContactDetails } from "../Details/useFetchContactDetails";
import { ContactItemIdentifier } from "./List/ContactItem/ContactItem";
import { Action, reduceToAvailableActions } from "client_legacy/ndaw/actions";
import styles from "./DetailsPanel.module.less";
import { ContactSource } from "client/Api/Email/contacts";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type DetailsPanelProps<ActionContext> = {
	itemId: ContactItemIdentifier | null;
	actions: readonly Action<ActionContext>[];
	actionContext: ActionContext;
};

export const DetailsPanel = <ActionContext, >(props: DetailsPanelProps<ActionContext>): ReactElement => {
	const {
		itemId,
		actions,
		actionContext
	} = props;

	const renderContent = (): ReactElement => {
		if (itemId) {
			return (
				<>
					<ContactDetails
						itemId={itemId}
					/>
					<AiAssistantFabSpacing />
				</>
			);
		};

		return (
			<NoResults
				trackingId="no-contact-selected"
				icon="address-book"
				title={localize("_NoContactSelected")}
				message={localize("_ThereIsCurrentlyNoContactSelected")}
				height="fill"
			/>
		);
	};

	const hasAvailableActions: boolean = useMemo(
		(): boolean => {
			return reduceToAvailableActions(
				actions,
				actionContext
			).length > 0;
		},
		[
			actionContext,
			actions
		]
	);

	return (
		<div className={styles.root}>
			{hasAvailableActions && (
				<div className={styles.actionBar}>
					<ContextActionBar
						actions={actions}
						context={actionContext}
						horizontalAlignment="no"
						trackingId="contact-action-bar"
					/>
				</div>
			)}
			<div className={styles.content}>
				{renderContent()}
			</div>
		</div>
	);
};
DetailsPanel.displayName = "DetailsPanel";

type ContactDetailsProps = {
	itemId: ContactItemIdentifier;
};

const ContactDetails = (props: ContactDetailsProps): ReactElement => {
	const {
		itemId
	} = props;

	const {
		state,
		reload
	} = useFetchContactDetails(itemId.source, itemId.id);

	switch (state.kind) {
		case "loading":
			return <BusyIndicator />;
		case "error":
			if (state.errorKind === "not-found"
				&& itemId.source === ContactSource.Workspace
			) {
				return (
					<NoResults
						icon="user"
						title={localize("_UserDoesNotExist")}
						message={localize("_TheUserIsDeletedOrInactive")}
						trackingId="loading-error"
						height="fill"
					/>
				);
			} else {
				return (
					<NoResults
						icon="gear"
						title={localize("_FailedToRetrieveTheContactDetails")}
						message={localize("_WeWereUnableToRetrieveContactDetails")}
						onRetry={reload}
						trackingId="loading-error"
						height="fill"
					/>
				);
			}
		case "loaded":
			return (
				<Details
					contact={state.contact}
				/>
			);
		default:
			never(state);
	}
};
ContactDetails.displayName = "ContactDetails";
